import { Controller } from "@hotwired/stimulus"

// Mounted on both unties/addresses/edit (domestic) and unties/international_addresses/edit
// (international), which render different fields, so every target below is `hasXTarget`-guarded.
// Field validity and the submit aggregate belong to the `validation` controller mounted alongside
// this one; this controller owns the rest:
//   - the phone country-code select syncing to the address "country" select, and to a session-
//     remembered value on load (international only)
//   - the US-state / international-state wrapper toggle, keyed off the selected country
//   - the tax-ID field's conditional visibility + tooltip text (international only, countries in
//     Gift::COUNTRIES_THAT_REQUIRE_TAX_ID)
//   - the India address-format warning banner (international only)
//   - the alcohol confirmation modal (domestic only, `@product.alcohol?`)
//
// A conditionally-shown field (tax ID, the two state wrappers) is hidden AND disabled rather than
// removed from the DOM - a disabled field is excluded from both native form submission and the
// `validation` controller's submit aggregate (see that controller's `updateSubmit`), reproducing
// what removing the element from the DOM entirely would.
export default class extends Controller {
  static get targets() {
    return [
      "country", "phoneCountryCode", "countryCodeHidden", "phoneNumber",
      "usStateWrapper", "internationalStateWrapper",
      "taxIdContainer", "taxIdWrapper", "taxIdTooltip", "indiaWarning",
      "alcoholModal", "alcoholAgeCheckbox", "alcoholDeliveryCheckbox",
      "alcoholAgeHidden", "alcoholDeliveryHidden", "alcoholContinueButton"
    ];
  }

  // Absent (domestic view, which has no tax-ID concept at all) means "no country requires a tax
  // ID" - taxIdCountries() below treats an empty string the same as a comma list with no matches.
  static get values() {
    return { taxCountries: { type: String, default: "" } };
  }

  connect() {
    this.pendingTimeouts = [];

    // Drop the server's pre-paint hiding; countryChanged below owns visibility from here.
    this.element.querySelectorAll(".js-country-hidden").forEach((el) => el.classList.remove("js-country-hidden"));

    if (this.hasCountryTarget) {
      this.countryChanged();
    }

    if (this.hasPhoneCountryCodeTarget) {
      this.scheduleSessionCountryCodeRestore();
    }

    if (this.hasAlcoholModalTarget) {
      this.scheduleAlcoholModal();
      this.updateAlcoholContinueButton();
    }
  }

  disconnect() {
    this.pendingTimeouts.forEach((id) => clearTimeout(id));
    this.pendingTimeouts = [];
  }

  // Address "country" select changed, or the initial value replayed once at connect.
  countryChanged() {
    const newCountry = this.countryTarget.value;

    this.toggleStateWrappers(newCountry);
    this.toggleTaxIdField(newCountry);
    this.toggleIndiaWarning(newCountry);
    this.maybeShowTaxIdTooltip(newCountry);

    if (this.hasPhoneCountryCodeTarget) {
      this.phoneCountryCodeTarget.value = newCountry;
      this.syncPhoneCountryCodeFields();
    }

    this.refreshValidation();
  }

  // Every enable/disable above lands after `validation` last computed its aggregate (it connects
  // first, and `input` fires before `change`), so recompute or Next stays stale.
  refreshValidation() {
    const validation = this.application.getControllerForElementAndIdentifier(this.element, "validation");

    if (validation) { validation.updateSubmit(); }
  }

  // The phone country-code select changed directly (user-driven).
  phoneCountryCodeChanged() {
    this.syncPhoneCountryCodeFields();
  }

  toggleStateWrappers(newCountry) {
    const selectedUSA = newCountry === "US";

    if (this.hasUsStateWrapperTarget) {
      this.setWrapperActive(this.usStateWrapperTarget, selectedUSA);
    }

    if (this.hasInternationalStateWrapperTarget) {
      this.setWrapperActive(this.internationalStateWrapperTarget, !selectedUSA);
    }
  }

  // Hide AND disable, never hide alone: updateSubmit skips only disabled fields, so a hidden
  // required field gates Next with no way to satisfy it. Also keeps one gift[state] in the params.
  setWrapperActive(wrapper, active) {
    wrapper.style.display = active ? "" : "none";
    wrapper.querySelectorAll("input, select, textarea").forEach((field) => {
      field.disabled = !active;
    });
  }

  toggleTaxIdField(newCountry) {
    if (!this.hasTaxIdWrapperTarget) { return; }

    const requiresTaxId = this.taxIdCountries().includes(newCountry);

    this.taxIdWrapperTarget.hidden = !requiresTaxId;
    this.taxIdWrapperTarget.querySelectorAll("input, select, textarea").forEach((field) => {
      field.disabled = !requiresTaxId;
    });
  }

  toggleIndiaWarning(newCountry) {
    if (!this.hasIndiaWarningTarget) { return; }

    this.indiaWarningTarget.style.display = newCountry === "IN" ? "" : "none";
  }

  maybeShowTaxIdTooltip(newCountry) {
    if (!this.hasTaxIdContainerTarget || !this.taxIdCountries().includes(newCountry)) { return; }

    const id = setTimeout(() => {
      if (!this.hasTaxIdTooltipTarget) { return; }

      const selectedOption = this.hasCountryTarget ? this.countryTarget.selectedOptions[0] : undefined;
      const countryName = selectedOption ? selectedOption.text : "";
      const toolTipText = `Tax ID must be entered for fulfillment to ${countryName}.`;

      $(this.taxIdTooltipTarget).attr("data-original-title", toolTipText).tooltip("fixTitle");
    }, 500);

    this.pendingTimeouts.push(id);
  }

  taxIdCountries() {
    return this.taxCountriesValue ? this.taxCountriesValue.split(",") : [];
  }

  syncPhoneCountryCodeFields() {
    const selected = this.phoneCountryCodeTarget.selectedOptions[0];

    if (this.hasCountryCodeHiddenTarget) {
      this.countryCodeHiddenTarget.value = selected ? selected.value : "";
    }

    if (this.hasPhoneNumberTarget && this.phoneNumberTarget.value.length <= 4) {
      // Per-option data (country_select ... format: :with_phone_code stamps one on every <option>)
      // - no single controller-root value could stand in for all of them, so this stays a direct
      // read (D59's per-item carve-out).
      const phoneCode = selected ? selected.dataset.phoneCode : undefined;
      this.phoneNumberTarget.value = "+" + phoneCode;
    }
  }

  // Mirrors setCountryCode()'s $(document).ready hook - a separate, later-firing sync that
  // restores a phone country code the recipient already chose in a previous session, reading
  // whatever the session-driven hidden field holds at the time it fires (which the country-driven
  // sync above may already have overwritten - same race the original jQuery had).
  scheduleSessionCountryCodeRestore() {
    const id = setTimeout(() => {
      if (this.hasCountryCodeHiddenTarget && this.countryCodeHiddenTarget.value) {
        this.phoneCountryCodeTarget.value = this.countryCodeHiddenTarget.value;
        this.syncPhoneCountryCodeFields();
      }
    }, 500);

    this.pendingTimeouts.push(id);
  }

  scheduleAlcoholModal() {
    const id = setTimeout(() => {
      $(this.alcoholModalTarget).modal({ backdrop: "static", keyboard: false });
      $(this.alcoholModalTarget).modal("show");
    });

    this.pendingTimeouts.push(id);
  }

  alcoholConfirmationChanged() {
    if (this.hasAlcoholAgeHiddenTarget) {
      this.alcoholAgeHiddenTarget.value = this.alcoholAgeCheckboxTarget.checked ? "true" : "false";
    }

    if (this.hasAlcoholDeliveryHiddenTarget) {
      this.alcoholDeliveryHiddenTarget.value = this.alcoholDeliveryCheckboxTarget.checked ? "true" : "false";
    }

    this.updateAlcoholContinueButton();
  }

  updateAlcoholContinueButton() {
    if (!this.hasAlcoholContinueButtonTarget) { return; }

    const confirmed = this.hasAlcoholAgeCheckboxTarget && this.alcoholAgeCheckboxTarget.checked &&
      this.hasAlcoholDeliveryCheckboxTarget && this.alcoholDeliveryCheckboxTarget.checked;

    this.alcoholContinueButtonTarget.disabled = !confirmed;
  }

  confirmAlcohol() {
    $(this.alcoholModalTarget).modal("hide");
  }
}
